import type { Dispute } from '@/api/disputes';
import type { DisputeStatus } from '@/api/generated/enums';
import type { StatusTone } from '@/components/StatusBadge';

/**
 * What the dispute screen may say, kept out of the component.
 *
 * <p>
 * The pair `queue.ts` makes for the approval side, and the same discipline every screen in this
 * project follows: the refusals are the server's, restated where a clerk can see them before the
 * 400 rather than after it. <strong>The server stays authoritative</strong> — if these disagree,
 * this is the one that is wrong.
 * </p>
 */

/** What a clerk is filling in when a customer objects. */
export interface DisputeDraft {
  bookingId: number | null;
  /** Null means the settlement as a whole, which is the commoner case at a counter. */
  rentalOrderItemId: number | null;
  claim: string;
  /** As typed, so a half-entered figure is a validation problem rather than a silent NaN. */
  disputedAmount: string;
}

export const emptyDraft: DisputeDraft = {
  bookingId: null,
  rentalOrderItemId: null,
  claim: '',
  disputedAmount: '',
};

/**
 * The amount as a number, or null when the box is empty.
 *
 * Undefined is the third answer and means "there is something in the box and it is not a number" —
 * which the caller must not send as zero, because a dispute over nothing is not a dispute.
 */
export function parseAmount(raw: string): number | null | undefined {
  const text = raw.trim();

  if (text === '') {
    return null;
  }

  const value = Number(text);

  return Number.isFinite(value) ? value : undefined;
}

/**
 * Why this dispute cannot be recorded yet, in the order a clerk would fix them.
 *
 * <p>
 * The claim is compulsory here for the reason it is compulsory on the server: a dispute with no
 * sentence beside it settles nothing later and is indistinguishable from a clerical slip.
 * </p>
 */
export function raiseProblems(draft: DisputeDraft): string[] {
  const problems: string[] = [];

  if (draft.bookingId === null) {
    problems.push('Find the booking this is about.');
  }

  if (draft.claim.trim() === '') {
    problems.push(
      'Write down what the customer says, in their words. It is what settles this later.',
    );
  }

  const amount = parseAmount(draft.disputedAmount);

  if (amount === undefined) {
    problems.push('The amount in question is not a number.');
  } else if (amount !== null && amount < 0) {
    problems.push('The amount in question cannot be negative.');
  }

  return problems;
}

/** How a resolution is being closed, and what the shop came down to if it did. */
export interface ResolutionDraft {
  status: Exclude<DisputeStatus, 'Open'>;
  resolution: string;
  settledAmount: string;
}

/**
 * Why this resolution cannot be submitted.
 *
 * <p>
 * The note is compulsory for the same reason the claim is: "rejected" alone answers no question
 * anybody will later ask of it.
 *
 * The settled figure is only meaningful on `Upheld` — a rejected dispute settled at the original
 * charge, and recording that would make the two indistinguishable. <strong>Here the client is
 * deliberately stricter than the server</strong>, which was checked live: `DisputeService.ResolveAsync`
 * silently drops a figure sent with any other status. Dropping it is the right thing for the record
 * and the wrong thing for the person, who typed an amount and was never told it went nowhere.
 * </p>
 */
export function resolveProblems(draft: ResolutionDraft): string[] {
  const problems: string[] = [];

  if (draft.resolution.trim() === '') {
    problems.push('Say why it ended this way. It is what the shop stands on if this comes back.');
  }

  const settled = parseAmount(draft.settledAmount);

  if (settled === undefined) {
    problems.push('What it was settled at is not a number.');
  } else if (settled !== null) {
    if (settled < 0) {
      problems.push('What it was settled at cannot be negative.');
    }

    if (draft.status !== 'Upheld') {
      problems.push(
        'Only an upheld dispute settles at a different figure. Clear the amount, or uphold it.',
      );
    }
  }

  return problems;
}

/**
 * What each way of closing a dispute means, said on the screen rather than left to a one-word enum.
 *
 * <p>
 * <strong>Withdrawn and Rejected are the pair worth spelling out.</strong> They are one keystroke
 * apart and mean opposite things about who decided: one is a decision the shop made and may be
 * asked to defend, the other is a customer who went away. A shop reading its own history a year
 * later cannot tell them apart from the status alone if the screen never said which was which.
 * </p>
 */
export const outcomeHints: Record<Exclude<DisputeStatus, 'Open'>, string> = {
  Upheld:
    'The customer was right. Record what the shop agreed to come down to — then correct the inspection separately, which is what actually moves the money.',
  Rejected: 'The shop stands by the charge. What you write here is what it stands on.',
  Withdrawn: 'The customer let it go. Not a decision the shop made, and recorded as such.',
};

export function statusTone(status: DisputeStatus): StatusTone {
  switch (status) {
    case 'Open':
      return 'warning';
    case 'Upheld':
      return 'brand';
    case 'Rejected':
      return 'negative';
    default:
      return 'neutral';
  }
}

/**
 * <strong>Upholding a dispute does not change what the customer is charged.</strong>
 *
 * <p>
 * The service says so in its own remarks and it is the thing most likely to be misread at a
 * counter: the log records what was agreed, and the charge is still moved by correcting the
 * inspection. A shop that upheld a dispute and stopped there has told the customer one figure and
 * billed them another — so the screen has to say it where the row is, not only in a drawer nobody
 * reopens.
 * </p>
 */
export function needsChargeCorrection(dispute: Dispute): boolean {
  return (
    dispute.status === 'Upheld' &&
    dispute.settledAmount !== null &&
    dispute.settledAmount !== dispute.disputedAmount
  );
}

/** What the queue is holding, said once above the list. */
export interface DisputeSummary {
  open: number;
  /** Money the shop is arguing about. Unknown for a dispute raised without a figure. */
  amountInQuestion: number;
  longestOpenDays: number;
}

export function summarise(disputes: readonly Dispute[]): DisputeSummary {
  const open = disputes.filter((dispute) => dispute.status === 'Open');

  return {
    open: open.length,
    amountInQuestion: open.reduce((sum, dispute) => sum + (dispute.disputedAmount ?? 0), 0),
    longestOpenDays: open.reduce((longest, dispute) => Math.max(longest, dispute.daysOpen ?? 0), 0),
  };
}
